import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect } from 'react';
import { Edit, Trash2, History, Store, Plus } from 'lucide-react';
import AddSucursalPage from '../../../components/Sucursal/AddSucursalPage';
import EditSucursalPage from '../../../components/Sucursal/EditSucursalPage';
import DeleteSucursalPage from '../../../components/Sucursal/DeleteSucursalPage';
import { fetchSucursales, type Sucursal } from '../../../services/pos/sucursalService';

// Imports agregados para replicar el diseño
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import Loading from '../../../components/Loading';

const SucursalesPage: React.FC = () => {
    // Estados originales
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSucursal, setSelectedSucursal] = useState<Sucursal | null>(null);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [loading, setLoading] = useState(true);

    // Estados y Hooks agregados para Paginación y Sesión
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();

    useEffect(() => {
        loadSucursales(true);
    }, []);

    const loadSucursales = async (showSpinner = true) => {
        try {
            if (showSpinner) {
                setLoading(true);
            }
            const data = await fetchSucursales();
            // Opcional: ordenar por ID si lo deseas consistente como en CodigoOrdenPage
            // const sortedData = [...data].sort((a, b) => a.id - b.id);
            setSucursales(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            if (showSpinner) {
                setLoading(false);
            }
        }
    };

    // Lógica de Paginación
    const totalPages = Math.ceil(sucursales.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedSucursales = sucursales.slice(startIndex, startIndex + itemsPerPage);

    const handleAddSucursal = () => {
        setShowAddModal(true);
    };

    const handleEditSucursal = (sucursal: Sucursal) => {
        setSelectedSucursal(sucursal);
        setShowEditModal(true);
    };

    const handleDeleteSucursal = (sucursal: Sucursal) => {
        setSelectedSucursal(sucursal);
        setShowDeleteModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedSucursal(null);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedSucursal(null);
    };

    const handleSucursalAdded = () => {
        loadSucursales(false);
    };

    const handleSucursalUpdated = () => {
        loadSucursales(false);
    };

    const handleSucursalDeleted = () => {
        loadSucursales(false);
    };

    if (loading && sucursales.length === 0) {
        return <Loading text="Cargando sucursales..." />;
    }

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Sucursales</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/sucursales" className="text-[#c1348b] hover:text-[#8f1f64]">Sucursales</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        style={{ height: '39px' }}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center text-sm font-medium transition-colors"
                        onClick={() => loadSucursales(true)}
                        title="Recargar sucursales"
                    >
                        <History className="w-5 h-5" />
                    </button>

                    <button
                        onClick={handleAddSucursal}
                        style={{ minWidth: '170.75px', height: '39px' }}
                        className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Agregar Sucursal
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Modal de Sesión Expirada Integrado */}
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direccion</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codigo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#61257b]"></div>
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando sucursales...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayedSucursales.length > 0 ? (
                                displayedSucursales.map((sucursal) => (
                                    <tr key={sucursal.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-900">{sucursal.nombre}</td>
                                        <td className="px-4 py-4 text-sm text-gray-900">{sucursal.direccion}</td>
                                        <td className="px-4 py-4 text-sm text-gray-900">{sucursal.codigo}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{sucursal.telefono || 'N/A'}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{sucursal.email || 'N/A'}</td>
                                        <td className="px-4 py-4">
                                            {sucursal.activo ? (
                                                <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Activo</span>
                                            ) : (
                                                <span className="px-2 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800">Inactivo</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditSucursal(sucursal)}
                                                    className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                    title="Editar sucursal"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSucursal(sucursal)}
                                                    className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                    title="Eliminar sucursal"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Store className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay sucursales disponibles
                                            </span>
                                            <p className="text-gray-500 text-sm">
                                                Agrega una nueva sucursal para comenzar
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {/* Componente de Paginación Integrado */}
                {!loading && sucursales.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={sucursales.length}
                    />
                )}
                {/* Modales Existentes */}
                <AddSucursalPage
                    isOpen={showAddModal}
                    onClose={handleCloseAddModal}
                    onSuccess={handleSucursalAdded}
                />

                {selectedSucursal && (
                    <EditSucursalPage
                        isOpen={showEditModal}
                        onClose={handleCloseEditModal}
                        onSuccess={handleSucursalUpdated}
                        sucursal={selectedSucursal}
                    />
                )}

                {selectedSucursal && (
                    <DeleteSucursalPage
                        isOpen={showDeleteModal}
                        onClose={handleCloseDeleteModal}
                        onSuccess={handleSucursalDeleted}
                        sucursal={selectedSucursal}
                    />
                )}
            </div>


        </div>
    );
};

export default SucursalesPage;
